import { WEBSITE_TEMPLATE_CATEGORIES, type WebsiteTemplateCategory } from '@dukanti/shared';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../hooks/use-website-t';

export interface TemplateCategoryFilterProps {
  /** Null is "All templates". */
  readonly value: WebsiteTemplateCategory | null;
  readonly onChange: (category: WebsiteTemplateCategory | null) => void;
}

const CATEGORY_KEYS: Readonly<Record<WebsiteTemplateCategory, string>> = {
  jewelry: 'admin.websiteTemplates.category.jewelry',
  fashion: 'admin.websiteTemplates.category.fashion',
  beauty: 'admin.websiteTemplates.category.beauty',
  electronics: 'admin.websiteTemplates.category.electronics',
  'home-living': 'admin.websiteTemplates.category.homeLiving',
  food: 'admin.websiteTemplates.category.food',
  other: 'admin.websiteTemplates.category.other',
};

/**
 * The business categories of the library: All templates, Jewelry, Fashion,
 * Beauty, Electronics, Home & Living, Food, Other. They name kinds of
 * business, never design styles. The filter narrows the list on the server.
 */
export function TemplateCategoryFilter({
  value,
  onChange,
}: TemplateCategoryFilterProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const chips: readonly {
    readonly category: WebsiteTemplateCategory | null;
    readonly label: string;
  }[] = [
    { category: null, label: t('admin.websiteTemplates.gallery.filter.all') },
    ...WEBSITE_TEMPLATE_CATEGORIES.map((category) => ({
      category,
      label: t(CATEGORY_KEYS[category]),
    })),
  ];

  return (
    <div
      role="group"
      aria-label={t('admin.websiteTemplates.gallery.filterLabel')}
      data-testid="template-category-filter"
      className="flex flex-wrap gap-2"
    >
      {chips.map(({ category, label }) => {
        const pressed = value === category;
        return (
          <button
            key={category ?? 'all'}
            type="button"
            aria-pressed={pressed}
            data-testid={`category-chip-${category ?? 'all'}`}
            onClick={() => onChange(category)}
            className={cn(
              'focus-visible:ring-ring/50 rounded-full border px-4 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:ring',
              pressed
                ? 'border-admin-lime bg-admin-lime text-primary-foreground'
                : 'border-input bg-card text-foreground hover:bg-admin-surface-muted',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
